import { useState } from 'react';
import { attention } from '@cwos/api-client';
import type { messaging } from '@cwos/api-client';
import { relativeTime } from '@cwos/ui-shared';
import { Avatar, Button, FilterSummary, Pill, SkeletonSet, SuccessEmpty } from '@cwos/ui-admin';
import { useAuth } from '../../lib/auth.js';
import { useNav } from '../navigation.js';
import { usePermissions } from '../permissions.js';
import {
  activityAt,
  channelTitle,
  mentionAttention,
  mentionCountsByChannel,
  useChannels,
  useMyMentions,
} from './messaging.js';
import { CreateChannel } from './CreateChannel.js';
import { LoadFailure } from '../LoadFailure.js';

type ChannelView = messaging.ChannelView;

/**
 * Messages — the conversation list (doc 01 comms). Grouped by kind with unread
 * counts + dot and a kind badge. Read paths run live; a 403 renders the doc-07
 * error state rather than hiding. No conversations are ever fabricated.
 */
export function Messages() {
  const { me } = useAuth();
  const navigate = useNav();
  const permissions = usePermissions();
  const { has } = permissions;
  /**
   * REVEALING the "New conversation" button. Default-deny is right for that and
   * the empty state must not read it as an answer — see below.
   */
  const canCreate = has('messaging:manage');
  const createUnknown = permissions.isLoading || permissions.isError;
  const [composing, setComposing] = useState(false);
  const query = useChannels();
  // The own-only unread-mentions read (F194 seam 3): the server returns YOUR
  // mentions and nothing anyone can widen. Best-effort for the LIST — a failed
  // badge read must never take down the conversation list — but NOT best-effort
  // for the sentence: see `mentionAttention`, and the paragraph below.
  const myMentions = useMyMentions();
  const mentionCounts = mentionCountsByChannel(myMentions.data?.mentions);
  const mentions = mentionAttention(myMentions);

  const scope = {
    what: 'your conversations',
    scope: me ? (me.orgName ?? me.orgId) : 'your organization',
    period: 'across your channels',
    narrowings: [],
  };

  const channels = query.data ?? [];
  const totalUnread = channels.reduce((sum, c) => sum + (c.unreadCount ?? 0), 0);
  /**
   * The line, or nothing.
   *
   * `null` for `unasked` and `none` ONLY. Silence here is a deliberate,
   * enumerated choice for two states and never a fallthrough for the rest: an
   * `attention` variant added later reaches `sentenceOf` and appears on screen,
   * which is the failure direction that gets noticed.
   */
  const mentionSentence =
    mentions.kind === 'unasked' || mentions.kind === 'none'
      ? null
      : attention.sentenceOf(mentions, attention.MENTIONS);

  return (
    <div>
      <div className="rn-card-head" style={{ marginBottom: 'var(--space-12)' }}>
        <h1 className="rn-card-title">Messages</h1>
        {canCreate ? (
          <Button
            variant={composing ? 'secondary' : 'accent'}
            small
            onClick={() => setComposing((v) => !v)}
          >
            {composing ? 'Close' : 'New conversation'}
          </Button>
        ) : null}
      </div>

      <FilterSummary scope={scope} />
      {/*
        THE MENTIONS HALF OF THIS LINE USED TO BE `data?.length ?? 0`.

        Four states arrived at that expression and one number left it: loading,
        a 403, a real zero, and a page capped at 50 out of an unknown larger
        number. The first two printed nothing, which on this screen reads as
        "nothing is waiting for you" — said on the strength of a request that
        had not come back, or one the account was refused. The fourth printed
        "50 mentions of you" as a total it had no way to know.

        `mentionAttention` keeps the five apart and `attention.sentenceOf` is the
        only thing that words them, so a future edit here cannot reword one into
        another. `unasked` and `none` stay silent — a real zero needs no line,
        and neither does a read still in flight — while `unavailable` and
        `refused` now SAY so, because those are the two a person must not read
        as an all-clear.
      */}
      {totalUnread > 0 || mentionSentence !== null ? (
        <p className="rn-muted" style={{ marginBottom: 'var(--space-12)' }}>
          {totalUnread > 0 ? `${totalUnread} unread across your channels.` : null}
          {totalUnread > 0 && mentionSentence !== null ? ' ' : null}
          {mentionSentence}
        </p>
      ) : null}

      {canCreate && composing ? (
        <div style={{ marginBottom: 'var(--space-16)' }}>
          <CreateChannel
            onCreated={(id) => {
              setComposing(false);
              navigate(`/messages/${id}`);
            }}
            onCancel={() => setComposing(false)}
          />
        </div>
      ) : null}

      {query.isLoading ? (
        <SkeletonSet surface="table" rows={6} />
      ) : query.isError && !query.data ? (
        <LoadFailure
          error={query.error}
          message="We couldn’t load your conversations just now."
          onRetry={() => void query.refetch()}
        />
      ) : channels.length === 0 ? (
        <SuccessEmpty
          heading="No conversations yet"
          message={
            // The affordance the sentence points at must exist for THIS reader:
            // "New conversation" is a `messaging:manage` button, and telling
            // everyone else to click it describes someone else's screen.
            //
            // THE SECOND SENTENCE IS AN ANSWER AND NEEDS A RESOLVED SET. "When
            // someone starts one with you" tells the reader that starting one
            // is not theirs to do — it is the refusal, worded gently — and
            // `canCreate` is false while `/auth/me` is in flight and false
            // after it fails. An `org_admin` on a cold load was told to wait
            // for somebody else to message them, beside a button they hold.
            // Neither sentence is known yet, so neither is said.
            createUnknown
              ? 'Conversations you are part of will appear here.'
              : canCreate
                ? 'Start one with “New conversation” to reach your team.'
                : 'When someone starts one with you, it will appear here.'
          }
        />
      ) : (
        <ChannelGroups
          channels={channels}
          mentionCounts={mentionCounts}
          onOpen={(id) => navigate(`/messages/${id}`)}
        />
      )}
    </div>
  );
}

/**
 * ONE conversation list (Phase F — owner directive: people/conversations
 * first, an inbox that reads like a messaging app, not channel
 * administration). Most recent activity first, the order a person scans a
 * conversation list in; unread is SHOWN, not sorted — bumping unread rows to
 * the top destroyed recency, and the dot + count + weight already make unread
 * unmissable in place. The kind still shows itself visually — a face for a
 * person, `#` for a group, `!` for an announcement — but the taxonomy pills
 * are gone: internal channel vocabulary is not something a reader needs to
 * pass an inbox.
 */
function ChannelGroups({
  channels,
  mentionCounts,
  onOpen,
}: {
  channels: ChannelView[];
  mentionCounts: Map<string, number>;
  onOpen: (id: string) => void;
}) {
  const rows = [...channels].sort((a, b) => Date.parse(activityAt(b)) - Date.parse(activityAt(a)));
  return (
    <section className="rn-card msg-group">
      <ul className="msg-list">
        {rows.map((channel) => (
          <ChannelRow
            key={channel.id}
            channel={channel}
            mentionCount={mentionCounts.get(channel.id) ?? 0}
            onOpen={onOpen}
          />
        ))}
      </ul>
    </section>
  );
}

function ChannelRow({
  channel,
  mentionCount,
  onOpen,
}: {
  channel: ChannelView;
  /** Unread mentions of YOU in this channel, from the own-only read. */
  mentionCount: number;
  onOpen: (id: string) => void;
}) {
  const unread = channel.unreadCount ?? 0;
  // The conversation's own clock: its last message, or its creation when it has
  // none yet. NOT `lastReadAt` — "Last read 3d ago" was a fact about the reader
  // dressed as a fact about the conversation.
  const activity = relativeTime(new Date(activityAt(channel)));
  const preview = channel.lastMessage?.preview?.trim();

  return (
    <li>
      <button
        type="button"
        className="msg-channel-row"
        data-unread={unread > 0}
        data-muted={channel.muted}
        onClick={() => onOpen(channel.id)}
      >
        <span className="msg-channel-dot" aria-hidden="true" data-on={unread > 0} />
        {/* A face on a direct message, a sigil on a channel, in the same box.
            Decorative — the title is right beside it in text — but it is what
            makes three separately-headed groups read as one list instead of
            three tables. */}
        {channel.kind === 'dm' ? (
          <Avatar name={channelTitle(channel)} size={32} />
        ) : (
          <span
            className="msg-channel-glyph"
            data-kind={channel.kind}
            role="img"
            aria-label={channel.kind === 'announcement' ? 'Announcement' : 'Group conversation'}
          >
            {channel.kind === 'announcement' ? '!' : '#'}
          </span>
        )}
        <span className="msg-channel-main">
          <span className="msg-channel-head">
            <span className="msg-channel-title">{channelTitle(channel)}</span>
            <time className="rn-muted msg-channel-time" dateTime={activity.absolute}>
              {activity.short}
            </time>
          </span>
          <span className="msg-channel-preview">
            {preview ?? <span className="rn-muted">No messages yet</span>}
          </span>
          {channel.status === 'archived' || channel.muted ? (
            <span className="msg-channel-sub">
              {channel.status === 'archived' ? <Pill variant="plain">Archived</Pill> : null}
              {channel.muted ? <span className="rn-muted">Muted</span> : null}
            </span>
          ) : null}
        </span>
        {mentionCount > 0 ? (
          <span
            className="msg-mention-badge"
            aria-label={`${mentionCount} ${mentionCount === 1 ? 'mention' : 'mentions'} of you`}
          >
            @{mentionCount}
          </span>
        ) : null}
        {unread > 0 ? (
          <span className="msg-unread-count" aria-label={`${unread} unread`}>
            {unread}
          </span>
        ) : null}
      </button>
    </li>
  );
}
